/**
 * ROTATION PAIR FINDER — find FPL player pairs that rotate well by FDR.
 * Green = FDR 1-2, Amber = FDR 3 (tolerated), Red = FDR 4-5, Blank = black.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowsClockwise, ArrowsLeftRight, Funnel, Info, LinkSimple,
    MagnifyingGlass, Plus, UserSwitch,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POOL_LS_KEY = "timing.pool.v1";

const COLOR = {
    green: { bg: "#2E9E5B", text: "#fff", label: "FDR 1–2" },
    amber: { bg: "#C9A227", text: "#0a0a0a", label: "FDR 3" },
    red: { bg: "#C82C22", text: "#fff", label: "FDR 4–5" },
    blank: { bg: "#111", text: "#666", label: "Blank" },
};

const POS_LABEL = { GKP: "Brankáři", DEF: "Obránci", MID: "Záložníci", FWD: "Útočníci" };

function autoText(t, gws) {
    const n = gws.length;
    const parts = [`Tato dvojice poskytuje ${t.green_weeks} zelených zápasů z ${n} Gameweeků.`];
    if (t.green_run > 1) {
        const colors = t.colors;
        let best = 0, cur = 0, end = -1;
        colors.forEach((c, i) => {
            cur = c === "green" ? cur + 1 : 0;
            if (cur > best) { best = cur; end = i; }
        });
        if (end >= 0) parts.push(`Nejdelší zelená série trvá od GW${gws[end - best + 1]} do GW${gws[end]}.`);
    }
    if (t.amber_weeks > 0) parts.push(`V ${t.amber_weeks} ${t.amber_weeks === 1 ? "kole" : "kolech"} je nutné přijmout FDR 3.`);
    parts.push(t.red_weeks === 0 ? "Dvojice nemá žádný červený Gameweek." : `Pozor: ${t.red_weeks}× červený/blank Gameweek.`);
    return parts.join(" ");
}

function addBothToPool(pa, pb) {
    let pool = [];
    try { pool = JSON.parse(localStorage.getItem(POOL_LS_KEY)) || []; } catch { /* noop */ }
    const ids = new Set(pool.map((p) => p.fpl_id));
    let added = 0;
    for (const p of [pa, pb]) {
        if (ids.has(p.id)) continue;
        if (pool.length >= 100) { toast.error("Watchlist je plný (100)"); break; }
        pool.push({
            fpl_id: p.id, historical_slug: null, display_name: p.name,
            position: p.position, team: p.team, current_price: p.price, ownership_pct: null,
        });
        added += 1;
    }
    localStorage.setItem(POOL_LS_KEY, JSON.stringify(pool));
    toast.success(added ? `Přidáno ${added} hráčů do watchlistu (Timing → Team Season DNA)` : "Oba hráči už ve watchlistu jsou");
}

export default function RotationPairFinder() {
    const [sp, setSp] = useSearchParams();
    const [startGw, setStartGw] = useState(parseInt(sp.get("startGW"), 10) || 1);
    const [endGw, setEndGw] = useState(parseInt(sp.get("endGW"), 10) || 8);
    const [position, setPosition] = useState(sp.get("positions") || "DEF");
    const [maxCombined, setMaxCombined] = useState(sp.get("budget") || "");
    const [maxPrice, setMaxPrice] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [availability, setAvailability] = useState("available");
    const [maxFdr, setMaxFdr] = useState(parseInt(sp.get("maxFDR"), 10) || 3);
    const [maxAmber, setMaxAmber] = useState("");
    const [mode, setMode] = useState(sp.get("selectedPlayer") ? "partner" : "all");
    const [playerId, setPlayerId] = useState(sp.get("selectedPlayer") ? parseInt(sp.get("selectedPlayer"), 10) : null);
    const [playerQuery, setPlayerQuery] = useState("");
    const [playerList, setPlayerList] = useState([]);
    const [sort, setSort] = useState("green_run");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setBusy(true); setErr(null);
        try {
            const params = new URLSearchParams({
                start_gw: String(startGw), end_gw: String(endGw), position,
                availability, max_fdr: String(maxFdr), sort, limit: "20",
            });
            if (maxCombined) params.set("max_combined", maxCombined);
            if (maxPrice) params.set("max_price", maxPrice);
            if (minPrice) params.set("min_price", minPrice);
            if (maxAmber !== "") params.set("max_amber", maxAmber);
            if (mode === "partner" && playerId) params.set("player_id", String(playerId));
            const r = await axios.get(`${API}/rotation/pairs?${params}`);
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Načtení selhalo");
        } finally {
            setBusy(false);
        }
    }, [startGw, endGw, position, availability, maxFdr, sort, maxCombined, maxPrice, minPrice, maxAmber, mode, playerId]);

    useEffect(() => { load(); }, [load]);

    // players for the partner picker
    useEffect(() => {
        axios.get(`${API}/rotation/players?position=${position}`)
            .then((r) => setPlayerList(r.data.players || []))
            .catch(() => {});
    }, [position]);

    const share = () => {
        const p = new URLSearchParams({
            startGW: String(startGw), endGW: String(endGw), positions: position, maxFDR: String(maxFdr),
        });
        if (maxCombined) p.set("budget", maxCombined);
        if (mode === "partner" && playerId) p.set("selectedPlayer", String(playerId));
        setSp(p, { replace: true });
        const url = `${window.location.origin}/rotation?${p.toString()}`;
        try {
            const p2 = navigator.clipboard?.writeText(url);
            if (p2 && typeof p2.catch === "function") p2.catch(() => { /* ignore */ });
        } catch { /* ignore */ }
        toast.success("Odkaz zkopírován do schránky");
    };

    const quickRange = (n) => { setStartGw(1); setEndGw(n === 38 ? 38 : Math.min(38, n)); };

    const filteredPlayers = useMemo(() => {
        const q = playerQuery.toLowerCase();
        return playerList.filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 12);
    }, [playerList, playerQuery]);

    const lockedPlayer = useMemo(
        () => playerList.find((p) => p.id === playerId), [playerList, playerId]);

    const gws = data?.gws || [];

    return (
        <AppShell>
            <section className="space-y-5" data-testid="rotation-page">
                <header>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2 mb-1">
                        <ArrowsLeftRight size={11} weight="fill" />
                        Planning · Rotace
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
                        Rotation <span className="text-fa-mint">Pair Finder</span>
                    </h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl leading-relaxed">
                        Najdi dvojice hráčů, kteří se střídají podle FDR — každý GW startuje ten s lehčím zápasem.
                        <strong className="text-fa-mint"> Zelená = FDR 1–2</strong>,
                        <strong className="text-yellow-300"> amber = FDR 3 (tolerovaná)</strong>,
                        <strong className="text-red-300"> červená = FDR 4–5</strong>. Řadíme primárně podle skutečně zelených sérií.
                    </p>
                    {data && (
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-1">
                            Data updated: {new Date(data.data_updated).toLocaleString("cs")} · zdroj {data.source === "fpl" ? "oficiální FPL FDR" : "Sportmonks rozpis 2026-27 (síla soupeře)"}
                        </div>
                    )}
                </header>

                {/* Filters */}
                <div className="border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="rotation-filters">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/45 flex items-center gap-1.5">
                            <Funnel size={11} weight="fill" /> GW
                        </span>
                        <input type="number" min={1} max={38} value={startGw}
                               onChange={(e) => setStartGw(Math.max(1, Math.min(38, parseInt(e.target.value, 10) || 1)))}
                               data-testid="rotation-start-gw"
                               className="w-16 bg-black/40 border border-white/15 px-2 py-1 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        <span className="text-white/40">–</span>
                        <input type="number" min={1} max={38} value={endGw}
                               onChange={(e) => setEndGw(Math.max(1, Math.min(38, parseInt(e.target.value, 10) || 8)))}
                               data-testid="rotation-end-gw"
                               className="w-16 bg-black/40 border border-white/15 px-2 py-1 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        {[["5 GW", 5], ["8 GW", 8], ["10 GW", 10], ["Celá sezóna", 38]].map(([l, n]) => (
                            <button key={n} type="button" onClick={() => quickRange(n)}
                                    data-testid={`rotation-quick-${n}`}
                                    className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-white/15 text-white/60 hover:text-white hover:border-fa-mint/50">
                                {l}
                            </button>
                        ))}
                        <span className="mx-1 text-white/20">|</span>
                        {Object.entries(POS_LABEL).map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setPosition(k)}
                                    data-testid={`rotation-pos-${k}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${position === k ? "bg-fa-mint text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {k}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                            Max Σ£
                            <input type="number" step={0.5} value={maxCombined} placeholder="any"
                                   onChange={(e) => setMaxCombined(e.target.value)}
                                   data-testid="rotation-max-combined"
                                   className="w-16 bg-black/40 border border-white/15 px-1.5 py-0.5 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        </label>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                            Max £/hráč
                            <input type="number" step={0.5} value={maxPrice} placeholder="any"
                                   onChange={(e) => setMaxPrice(e.target.value)}
                                   data-testid="rotation-max-price"
                                   className="w-16 bg-black/40 border border-white/15 px-1.5 py-0.5 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        </label>
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                            Min £/hráč
                            <input type="number" step={0.5} value={minPrice} placeholder="any"
                                   onChange={(e) => setMinPrice(e.target.value)}
                                   data-testid="rotation-min-price"
                                   className="w-16 bg-black/40 border border-white/15 px-1.5 py-0.5 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        </label>
                        <span className="mx-1 text-white/20">|</span>
                        {[["all", "Všichni"], ["available", "Jen dostupní"], ["starters", "Jen pravděpodobní starteři"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setAvailability(k)}
                                    data-testid={`rotation-avail-${k}`}
                                    className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${availability === k ? "bg-fa-sky text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {l}
                            </button>
                        ))}
                        <span className="mx-1 text-white/20">|</span>
                        {[[2, "Strict (max FDR 2)"], [3, "Practical (max FDR 3)"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setMaxFdr(k)}
                                    data-testid={`rotation-maxfdr-${k}`}
                                    className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${maxFdr === k ? "bg-fa-yellow text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {l}
                            </button>
                        ))}
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                            Max FDR3 týdnů
                            <input type="number" min={0} value={maxAmber} placeholder="any"
                                   onChange={(e) => setMaxAmber(e.target.value)}
                                   data-testid="rotation-max-amber"
                                   className="w-14 bg-black/40 border border-white/15 px-1.5 py-0.5 font-mono text-[11px] tabular-nums focus:outline-none focus:border-fa-mint" />
                        </label>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {[["all", "Všechny dvojice"], ["partner", "Najít parťáka pro hráče"]].map(([k, l]) => (
                            <button key={k} type="button"
                                    onClick={() => { setMode(k); if (k === "all") setPlayerId(null); }}
                                    data-testid={`rotation-mode-${k}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${mode === k ? "bg-fa-mint text-black font-bold" : "border border-white/10 text-white/60 hover:text-white"}`}>
                                {k === "partner" && <UserSwitch size={11} weight="bold" />}
                                {l}
                            </button>
                        ))}
                        {mode === "partner" && (
                            <span className="relative">
                                <MagnifyingGlass size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                                <input type="text" value={lockedPlayer ? lockedPlayer.name : playerQuery}
                                       onChange={(e) => { setPlayerId(null); setPlayerQuery(e.target.value); }}
                                       placeholder="Hledej hráče…"
                                       data-testid="rotation-player-search"
                                       className="bg-black/40 border border-fa-mint/40 pl-7 pr-2 py-1 font-mono text-[11px] w-56 focus:outline-none focus:border-fa-mint" />
                                {!playerId && playerQuery.length >= 2 && (
                                    <div className="absolute top-full left-0 mt-1 w-72 max-h-64 overflow-y-auto bg-[#0d0d0d] border border-white/20 z-30">
                                        {filteredPlayers.map((p) => (
                                            <button key={p.id} type="button"
                                                    onClick={() => { setPlayerId(p.id); setPlayerQuery(""); }}
                                                    data-testid={`rotation-player-opt-${p.id}`}
                                                    className="w-full text-left px-2 py-1.5 font-mono text-[11px] hover:bg-fa-mint/10 flex justify-between">
                                                <span>{p.name} <span className="text-white/40 text-[9px]">{p.team_short}</span></span>
                                                <span className="text-white/50 tabular-nums">{p.price != null ? `£${p.price}` : "—"}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </span>
                        )}
                        <span className="ml-auto flex items-center gap-2">
                            <select value={sort} onChange={(e) => setSort(e.target.value)}
                                    data-testid="rotation-sort"
                                    className="bg-black/40 border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white/70 focus:outline-none">
                                <option value="green_run">Longest Green Run</option>
                                <option value="coverage">Best Overall Coverage</option>
                                <option value="avg_fdr">Lowest Average FDR</option>
                                <option value="cheapest">Cheapest Pair</option>
                                <option value="value">Best Value Pair</option>
                                <option value="home_starts">Most Home Starts</option>
                            </select>
                            <button type="button" onClick={share} data-testid="rotation-share"
                                    className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-fa-sky/40 text-fa-sky hover:bg-fa-sky hover:text-black flex items-center gap-1">
                                <LinkSimple size={11} weight="bold" /> Sdílet
                            </button>
                        </span>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 flex-wrap font-mono text-[9px] uppercase tracking-widest text-white/50">
                    <Info size={11} weight="bold" className="text-fa-mint" />
                    {Object.entries(COLOR).map(([k, c]) => (
                        <span key={k} className="inline-flex items-center gap-1.5">
                            <span className="inline-block w-3 h-3" style={{ backgroundColor: c.bg }} />
                            {c.label} ({k})
                        </span>
                    ))}
                    <span className="text-white/35" title="Nejdelší souvislá série GW se selectedFDR < 3">Green Run = souvislá série FDR 1–2</span>
                    <span className="text-white/35" title="Počet GW se selectedFDR ≤ 3 / vybrané GW">Safe Coverage = podíl GW ≤ FDR 3</span>
                </div>

                {err && <div className="border border-red-300/40 bg-red-300/10 p-3 font-mono text-[11px] text-red-200">{String(err)}</div>}

                {busy && (
                    <div className="space-y-3" data-testid="rotation-skeleton">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="border border-white/10 bg-white/[0.02] p-4 animate-pulse h-32" />
                        ))}
                    </div>
                )}

                {!busy && data && data.pairs.length === 0 && (
                    <div className="border border-dashed border-white/15 bg-[#0d0d0d] p-8 text-center font-mono text-[11px] text-white/50" data-testid="rotation-empty">
                        Žádné dvojice nesplňují filtry — zkus Practical FDR 3, širší rozsah GW nebo vyšší rozpočet.
                    </div>
                )}

                {!busy && data && data.pairs.map((t, idx) => {
                    const pp0 = t.player_pairs[0];
                    const key = `${t.team_a.short}-${t.team_b.short}`;
                    return (
                        <div key={key} className="border border-white/10 bg-[#0d0d0d]" data-testid={`rotation-card-${key}`}>
                            {/* Card header */}
                            <div className="p-3 flex items-center gap-3 flex-wrap border-b border-white/5">
                                <span className="font-mono text-[10px] text-fa-mint tabular-nums">#{idx + 1}</span>
                                <span className="font-display font-black text-lg uppercase tracking-tight">
                                    {t.team_a.short} <span className="text-white/40">+</span> {t.team_b.short}
                                </span>
                                {t.perfect_green_pair && <Badge cls="text-fa-mint border-fa-mint/50 bg-fa-mint/10">Perfect Green</Badge>}
                                {t.safe_pair && !t.perfect_green_pair && <Badge cls="text-yellow-300 border-yellow-300/40 bg-yellow-500/10">Safe Pair</Badge>}
                                {t.broken_pair && <Badge cls="text-red-300 border-red-300/40 bg-red-500/10">Broken</Badge>}
                                <span className="ml-auto flex items-center gap-4 font-mono text-[10px] tabular-nums">
                                    <Metric label="Green Run" value={t.green_run} cls="text-fa-mint" />
                                    <Metric label="G/A/R" value={`${t.green_weeks}/${t.amber_weeks}/${t.red_weeks}`} cls="text-white/80" />
                                    <Metric label="Coverage" value={t.safe_coverage} cls="text-fa-sky" />
                                    <Metric label="Ø FDR" value={t.avg_selected_fdr ?? "—"} cls="text-fa-yellow" />
                                    <Metric label="Doma" value={t.home_starts} cls="text-white/60" />
                                    <Metric label="Σ£" value={pp0?.combined_price != null ? pp0.combined_price : "—"} cls="text-fa-yellow font-bold" />
                                </span>
                            </div>

                            {/* Timeline */}
                            <div className="p-3 overflow-x-auto">
                                <div className="flex gap-1 min-w-max">
                                    {t.timeline.map((cell) => {
                                        const c = COLOR[cell.color] || COLOR.blank;
                                        const startEntry = cell.start === "a" ? cell.a : cell.start === "b" ? cell.b : null;
                                        const benchEntry = cell.start === "a" ? cell.b : cell.start === "b" ? cell.a : null;
                                        const startTeam = cell.start === "a" ? t.team_a.short : t.team_b.short;
                                        const benchTeam = cell.start === "a" ? t.team_b.short : t.team_a.short;
                                        return (
                                            <div key={cell.gw} className="w-[86px] shrink-0" data-testid={`rotation-gw-${key}-${cell.gw}`}>
                                                <div className="font-mono text-[8px] uppercase tracking-widest text-white/40 text-center mb-0.5">GW{cell.gw}</div>
                                                <div className="border-2 p-1 text-center"
                                                     style={{ backgroundColor: c.bg, color: c.text, borderColor: "rgba(255,255,255,0.25)" }}>
                                                    {startEntry ? (
                                                        <>
                                                            <div className="font-mono text-[7px] uppercase tracking-widest opacity-75">START · {startTeam}{startEntry.dgw ? " · DGW" : ""}</div>
                                                            <div className="font-display font-bold text-[11px] leading-tight">{startEntry.label}</div>
                                                            <div className="font-mono text-[8px] opacity-90">FDR {startEntry.fdr}</div>
                                                        </>
                                                    ) : (
                                                        <div className="font-mono text-[9px] py-1.5">OBA BLANK</div>
                                                    )}
                                                </div>
                                                <div className="border border-white/10 bg-black/40 p-1 text-center opacity-60 mt-0.5">
                                                    {benchEntry ? (
                                                        <>
                                                            <div className="font-mono text-[7px] uppercase tracking-widest">BENCH · {benchTeam}{benchEntry.dgw ? " · DGW" : ""}</div>
                                                            <div className="font-mono text-[9px]">{benchEntry.label} · {benchEntry.fdr}</div>
                                                        </>
                                                    ) : (
                                                        <div className="font-mono text-[8px] py-1">BLANK</div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Player pairs + actions */}
                            <div className="px-3 pb-3 space-y-2">
                                <p className="font-mono text-[10px] text-white/55 leading-relaxed border-l-2 border-fa-mint/40 pl-2">
                                    {autoText(t, gws)}
                                </p>
                                <div className="space-y-1">
                                    {(expanded === key ? t.player_pairs : t.player_pairs.slice(0, 3)).map((pp, i) => (
                                        <div key={i} className="flex items-center gap-2 flex-wrap font-mono text-[11px] border border-white/5 bg-black/30 px-2 py-1"
                                             data-testid={`rotation-pair-${key}-${i}`}>
                                            <span className="text-fa-mint tabular-nums">{i + 1}.</span>
                                            <span className="font-bold text-white">{pp.player_a.name}</span>
                                            <span className="text-white/35 text-[9px]">{pp.player_a.team_short} · {pp.player_a.price != null ? `£${pp.player_a.price}` : "—"}</span>
                                            <span className="text-white/40">+</span>
                                            <span className="font-bold text-white">{pp.player_b.name}</span>
                                            <span className="text-white/35 text-[9px]">{pp.player_b.team_short} · {pp.player_b.price != null ? `£${pp.player_b.price}` : "—"}</span>
                                            <span className="ml-auto flex items-center gap-2">
                                                <span className="text-fa-yellow tabular-nums font-bold">{pp.combined_price != null ? `£${pp.combined_price}m` : "£ —"}</span>
                                                <span className="text-white/40 tabular-nums" title="Průměrná spolehlivost startu obou hráčů">rel {Math.round(pp.reliability * 100)}%</span>
                                                <button type="button" onClick={() => addBothToPool(pp.player_a, pp.player_b)}
                                                        data-testid={`rotation-add-pool-${key}-${i}`}
                                                        title="Přidat oba do watchlistu (Timing)"
                                                        className="text-fa-mint/70 hover:text-fa-mint"><Plus size={13} weight="bold" /></button>
                                                <button type="button"
                                                        onClick={() => { setMode("partner"); setPlayerId(pp.player_a.id); setPlayerQuery(""); }}
                                                        data-testid={`rotation-find-partner-${key}-${i}`}
                                                        title={`Najít jiného parťáka pro ${pp.player_a.name}`}
                                                        className="text-fa-sky/70 hover:text-fa-sky"><UserSwitch size={13} weight="bold" /></button>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {t.player_pairs.length > 3 && (
                                    <button type="button" onClick={() => setExpanded(expanded === key ? null : key)}
                                            data-testid={`rotation-expand-${key}`}
                                            className="font-mono text-[9px] uppercase tracking-widest text-white/50 hover:text-white">
                                        {expanded === key ? "Skrýt" : `Zobrazit všechny kombinace (${t.player_pairs.length})`}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                {!busy && data && (
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/35">
                        {data.total_pairs} týmových dvojic splňuje filtry · zobrazeno top {data.pairs.length}
                        <button type="button" onClick={load} className="ml-3 text-white/50 hover:text-white inline-flex items-center gap-1">
                            <ArrowsClockwise size={10} weight="bold" /> Obnovit
                        </button>
                    </div>
                )}
            </section>
        </AppShell>
    );
}

function Badge({ children, cls }) {
    return <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>{children}</span>;
}

function Metric({ label, value, cls }) {
    return (
        <span className="flex flex-col items-end leading-tight">
            <span className="text-[8px] uppercase tracking-widest text-white/35">{label}</span>
            <span className={cls}>{value}</span>
        </span>
    );
}
